import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Archive,
  Bell,
  BriefcaseBusiness,
  ChevronLeft,
  ClipboardList,
  FileText,
  LayoutGrid,
  LogOut,
  MessageSquareText,
  Network,
  ShieldCheck,
  Search,
  Settings,
  Users,
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { adminAuthService } from '../../services/admin/adminAuthService';
import { legalOperationsService } from '../../services/legalOperationsService';

const primaryItems = [
  { label: 'مركز العمليات', icon: LayoutGrid, path: '/admin/labor-intake', roles: ['super_admin', 'legal_manager', 'lawyer', 'legal_secretary', 'reviewer'] },
  { label: 'الطلبات الجديدة', icon: ClipboardList, path: '/admin/labor-intake?status=new', roles: ['super_admin', 'legal_manager', 'legal_secretary'] },
  { label: 'تحت المراجعة', icon: BriefcaseBusiness, path: '/admin/labor-intake?status=under_review', roles: ['super_admin', 'legal_manager', 'reviewer'] },
  { label: 'طلبات ناقصة', icon: FileText, path: '/admin/labor-intake?status=missing_required', roles: ['super_admin', 'legal_manager', 'legal_secretary'] },
  { label: 'المحالة للمحامين', icon: Archive, path: '/admin/labor-intake?status=assigned_lawyer', roles: ['super_admin', 'legal_manager'] },
];

const secondaryItems = [
  { label: 'نموذج التشغيل', icon: Network, path: '/admin/labor-intake/operating-model', roles: ['super_admin', 'legal_manager', 'reviewer'] },
  { label: 'المحادثات الواردة', icon: MessageSquareText, path: '/admin/labor-intake/messages', roles: ['super_admin', 'legal_manager', 'lawyer', 'legal_secretary'] },
  { label: 'العملاء', icon: Users, path: '/admin/labor-intake/clients', roles: ['super_admin', 'legal_manager', 'legal_secretary'] },
  { label: 'المرفقات', icon: FileText, path: '/admin/labor-intake/attachments', roles: ['super_admin', 'legal_manager', 'lawyer', 'legal_secretary', 'reviewer'] },
  { label: 'التقارير', icon: ClipboardList, path: '/admin/labor-intake/reports', roles: ['super_admin', 'legal_manager', 'reviewer'] },
  { label: 'المساعد التحليلي القانوني', icon: Network, path: '/admin/labor-intake/ai-assistant', roles: ['super_admin'], superOnly: true },
  { label: 'المستخدمون والصلاحيات', icon: ShieldCheck, path: '/admin/labor-intake/users', roles: ['super_admin', 'legal_manager'] },
  { label: 'إعدادات التشغيل', icon: Settings, path: '/admin/labor-intake/settings', roles: ['super_admin', 'legal_manager'] },
];

const LaborOperationsLayout = ({ children, searchValue = '', onSearchChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAdminAuth();
  const [platformRole, setPlatformRole] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (!token) return;
    legalOperationsService.getMetadata(token)
      .then((metadata) => setPlatformRole(metadata.role))
      .catch(() => setPlatformRole(null));
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    const loadMessageSummary = () => {
      legalOperationsService.getMessageSummary(token)
        .then((summary) => setUnreadMessages(summary.unreadMessages || 0))
        .catch(() => setUnreadMessages(0));
    };
    loadMessageSummary();
    const interval = window.setInterval(loadMessageSummary, 15000);
    return () => window.clearInterval(interval);
  }, [token, location.pathname]);

  const handleLogout = async () => {
    await adminAuthService.logout(token);
    logout();
    navigate('/cases/login');
  };

  const activePath = (path) => {
    const [pathname, query] = path.split('?');
    if (location.pathname !== pathname) return false;
    if (!query) return !location.search || location.pathname.includes('/labor-intake/');
    return location.search.includes(query);
  };

  return (
    <div className="labor-operations relative flex min-h-screen bg-[#f6f4ef] text-[#3b4758]" dir="rtl">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_78%_7%,rgba(175,153,119,0.065),transparent_30%),radial-gradient(circle_at_13%_82%,rgba(185,170,145,0.055),transparent_29%)]" />
      <aside className="sticky top-0 z-10 hidden h-screen w-[288px] shrink-0 flex-col bg-[#efede7]/80 text-[#293a50] backdrop-blur lg:flex">
        <div className="px-7 pb-7 pt-7">
          <img src="/images/wazin-logo.png" alt="Wazin" className="h-[66px] w-[156px] object-contain object-right mix-blend-multiply" />
          <div className="mt-4">
            <p className="text-[15px] font-bold text-[#25364f]">بوابة قضايا وازن</p>
            <p className="lo-kicker mt-2 font-bold">LEGAL OPERATIONS</p>
          </div>
        </div>

        <nav className="flex-1 px-4 py-4">
          <p className="mb-4 px-4 text-[12px] font-bold tracking-wide text-[#948066]">مسار القضايا العمالية</p>
          <div className="space-y-1.5">
            {primaryItems.filter((item) => !platformRole || item.roles.includes(platformRole)).map(({ label, icon: Icon, path }) => {
              const active = activePath(path);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => navigate(path)}
                  className={`group flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-right text-[15px] transition duration-200 ${
                    active ? 'bg-[#e4e0d7] font-bold text-[#293a50]' : 'text-[#647080] hover:bg-[#e9e6df] hover:text-[#293a50]'
                  }`}
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg transition ${active ? 'bg-[#f5f2ec]' : 'group-hover:bg-[#f5f2ec]'}`}>
                    <Icon className={`h-[18px] w-[18px] ${active ? 'text-[#948066]' : 'text-[#8b939d]'}`} />
                  </span>
                  <span className="flex-1">{label}</span>
                  {active && <ChevronLeft className="h-4 w-4 text-[#948066]" />}
                </button>
              );
            })}
          </div>

          <div className="mx-4 my-7 border-t border-[#e1ddd5]" />
          <p className="mb-4 px-4 text-[12px] font-bold tracking-wide text-[#948066]">المنصة</p>
          <div className="space-y-1">
            {secondaryItems.filter((item) => {
              if (item.superOnly && platformRole !== 'super_admin' && user?.role !== 'super_admin') return false;
              return !platformRole || item.roles.includes(platformRole);
            }).map(({ label, icon: Icon, path }) => (
              <button key={label} type="button" onClick={() => path && navigate(path)} className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-[15px] transition ${path && location.pathname === path ? 'bg-[#e4e0d7] font-bold text-[#293a50]' : 'text-[#687383] hover:bg-[#e9e6df] hover:text-[#293a50]'}`}>
                <Icon className="h-[18px] w-[18px] text-[#948066]" />
                <span className="flex-1 text-right">{label}</span>
                {path === '/admin/labor-intake/messages' && unreadMessages > 0 && (
                  <span className="rounded-full bg-[#293a50] px-2 py-0.5 text-[12px] font-bold text-white">{unreadMessages}</span>
                )}
              </button>
            ))}
          </div>
        </nav>

        <div className="mx-7 mb-6 border-t border-[#e1ddd5] pt-5">
          <p className="truncate text-sm font-bold text-[#25364f]">{user?.fullName || 'فريق وازن'}</p>
          <p className="mt-1 truncate text-[13px] text-[#806f58]">{user?.roleName || 'Legal Operations'}</p>
        </div>
      </aside>

      <div className="relative flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-[80px] items-center justify-between bg-[#f6f4ef]/90 px-5 shadow-[0_1px_0_rgba(70,58,42,0.065)] backdrop-blur-xl sm:px-10">
          <div className="flex items-center gap-3 lg:hidden">
            <img src="/images/wazin-logo.png" alt="Wazin" className="h-9 w-20 object-contain object-right mix-blend-multiply" />
            <div>
              <p className="text-sm font-bold text-[#25364f]">بوابة قضايا وازن</p>
              <p className="lo-gold text-[12px]">LEGAL OPERATIONS</p>
            </div>
          </div>
          <div className="lo-control hidden h-12 items-center gap-3 rounded-xl px-5 lg:flex lg:w-[420px]">
            <Search className="h-4 w-4 text-[#948066]" />
            <input
              value={searchValue}
              onChange={(event) => onSearchChange?.(event.target.value)}
              placeholder="بحث سريع في الملفات..."
              className="h-12 flex-1 bg-transparent text-[15px] text-[#3b4758] outline-none placeholder:text-[#918c84]"
            />
          </div>
          <div className="mr-auto flex items-center gap-2">
            <button type="button" onClick={() => navigate('/admin/labor-intake/messages')} className="relative rounded-xl p-3 text-[#697483] transition hover:bg-[#eeebe4]" title="المحادثات الواردة">
              <Bell className="h-[18px] w-[18px]" />
              {unreadMessages > 0 && <span className="absolute -left-1 -top-1 min-w-[20px] rounded-full bg-[#293a50] px-1.5 py-0.5 text-center text-[11px] font-bold text-white">{unreadMessages}</span>}
            </button>
            {unreadMessages > 0 && (
              <button type="button" onClick={() => navigate('/admin/labor-intake/messages')} className="hidden rounded-full bg-[#eee8dd] px-3 py-2 text-[12px] font-bold text-[#806f58] md:inline-flex">
                رسالة جديدة
              </button>
            )}
            <div className="mx-2 hidden h-7 border-r border-[#e4dccf] sm:block" />
            <div className="hidden text-left sm:block">
              <p className="max-w-[170px] truncate text-[14px] font-bold text-[#293a50]">{user?.fullName || 'فريق وازن'}</p>
              <p className="text-[13px] text-[#7b7a74]">{user?.roleName || 'Operations'}</p>
            </div>
            <button onClick={handleLogout} type="button" title="تسجيل الخروج" className="mr-2 rounded-xl p-2.5 text-[#9c927f] transition hover:bg-white hover:text-[#25364f]">
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

export default LaborOperationsLayout;
